import { createPromiseClient } from '@connectrpc/connect'
import { PlaceAutocompleteService } from '@fingertip/creator-proto/gen/fingertip/creator/place_autocomplete/v1/place_autocomplete_connect'
import { MagnifyingGlassIcon } from '@fingertip/icons'
import { getCookie } from 'cookies-next'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useDebounceValue } from 'usehooks-ts'

import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { FormControl } from '@/components/ui/form-control'
import { transport } from '@/lib/connect'
import { countries } from '@/lib/data/countries'
import { useToken } from '@/lib/hooks/use-token'
import { COUNTRY_CODE_KEY } from '@/lib/utils/constants'

export const constructDescription = ({
  name = '',
  vicinity = '',
  formattedAddress = '',
  types,
}: {
  name: string
  vicinity: string
  formattedAddress: string
  types: string[]
}) => {
  let description = name

  if (types && types.length > 0 && types.includes('establishment')) {
    // For establishments, include vicinity or formatted address
    description += `, ${vicinity ?? formattedAddress}`
  } else if (formattedAddress) {
    // For non-establishments, you might just want to include the formatted address
    description += `, ${formattedAddress}`
  }

  return description
}

const autocompleteClient = createPromiseClient(
  PlaceAutocompleteService,
  transport,
)

type Props = {
  onSelect: (value: ComboboxOption | undefined) => void
  defaultValue?: ComboboxOption
  defaultRegion?: string
  fallbackRegion?: string
  label?: ReactNode
  caption?: ReactNode
  control?: any
  onInputChange?: (value: string | undefined) => void
  onRegionChange?: (value: ComboboxOption) => void
  children?: ReactNode
  placeholder?: string
  disabled?: boolean
  hideAddress?: boolean
}

export const PlacesAutocomplete = ({
  label,
  caption,
  onSelect,
  defaultValue,
  defaultRegion,
  fallbackRegion = 'AU',
  onRegionChange,
  onInputChange,
  children,
  placeholder,
  hideAddress,
  disabled,
}: Props) => {
  const { t } = useTranslation()
  const countryToOptionMapper = (country: {
    emoji: string
    name: string
    iso: string
  }): ComboboxOption => ({
    id: country.iso,
    label: `${country.name}`,
  })

  const countryCode: any = getCookie(COUNTRY_CODE_KEY)

  const defaultCountry = countries.find(
    (country) =>
      (defaultRegion || countryCode || fallbackRegion) === country.iso,
  )!
  const defaultRegionOption: ComboboxOption =
    countryToOptionMapper(defaultCountry)

  const { callOptions, hasFetched } = useToken()
  const ref = useRef(null)
  const [value, setValue] = useState<ComboboxOption | undefined>(defaultValue)

  const [region, setRegion] = useState<ComboboxOption | undefined>(
    defaultRegionOption,
  )
  const [countryOptions, setCountryOptions] = useState(
    countries.map((country) => ({
      id: country.iso,
      label: `${country.name}`,
    })),
  )
  const [shouldFetch, setShouldFetch] = useState<boolean>(true)
  const [debouncedValue] = useDebounceValue<ComboboxOption | undefined>(
    value,
    300,
  )
  const [data, setData] = useState<ComboboxOption[] | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)

  // Fetch API (optional)
  useEffect(() => {
    async function loadData() {
      if (
        !debouncedValue ||
        !debouncedValue.label ||
        !shouldFetch ||
        loading ||
        !hasFetched
      ) {
        return
      }

      setLoading(true)

      try {
        const result = await autocompleteClient.getPlaceAutocomplete(
          {
            search: debouncedValue.label,
            region: region?.id ? String(region?.id) : undefined,
          },
          callOptions,
        )

        const predictions = JSON.parse(result.predictions) as any

        setData(
          (predictions || []).map((result: any) => ({
            label: result?.description,
            id: result?.place_id,
            result,
          })),
        )
      } catch (error: any) {
        toast.error(error.message)
      } finally {
        setLoading(false)
      }
    }

    // Only run query once user is logged in.
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue])

  const handleSelect = useCallback(
    ({ selectedItem }: any) => {
      onSelect(selectedItem)
      setShouldFetch(false)
      setValue(selectedItem)
    },
    [onSelect],
  )

  const handleClear = useCallback(() => {
    onSelect(undefined)
    setValue(undefined)
  }, [onSelect])

  const handleInputChange = useCallback(
    ({ inputValue }: { inputValue?: string }) => {
      setShouldFetch(true)
      setValue({ label: String(inputValue), id: '' })
      onInputChange?.(inputValue)
    },
    [onInputChange],
  )

  const handleRegionSelect = useCallback(
    ({ selectedItem }: any) => {
      setRegion(selectedItem)
      onRegionChange?.(selectedItem)
    },
    [onRegionChange],
  )

  const handleRegionInputChange = useCallback(
    ({ inputValue }: { inputValue?: string }) => {
      setCountryOptions(
        countries
          .filter((country) =>
            country.name
              .toLowerCase()
              .startsWith(inputValue ? inputValue.toLowerCase() : ''),
          )
          .map((country) => ({
            id: country.iso,
            label: `${country.name}`,
          })),
      )
    },
    [],
  )

  return (
    <div ref={ref} className="relative w-full">
      <FormControl name="region" label={t('country_or_region')}>
        <Combobox
          disabled={disabled}
          options={countryOptions}
          value={region}
          onInputChange={handleRegionInputChange}
          onChange={handleRegionSelect}
          placeholder={t('choose_a_country_or_region')}
        />
      </FormControl>

      {!hideAddress && (
        <FormControl name="address" caption={caption} label={label}>
          <Combobox
            disabled={disabled}
            options={data}
            value={value}
            isLoading={loading}
            onInputChange={handleInputChange}
            onChange={handleSelect}
            clearable
            onClear={handleClear}
            leftIcon={
              <MagnifyingGlassIcon className="size-5 text-muted-foreground" />
            }
            autoComplete="dont-autofill-address"
            placeholder={placeholder}
          >
            {children}
          </Combobox>
        </FormControl>
      )}
    </div>
  )
}
